// Loads world.json + city.glb and instantiates the city. One source mesh per unique model, one
// InstancedMesh per placement, everything frozen because the city never moves.

import { Scene } from '@babylonjs/core/scene';
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import '@babylonjs/loaders/glTF/2.0';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import { ASSET_BASE } from './config';
import { Ground } from '../sim/ground';

export interface WorldInstance { m: string; p: [number, number, number]; yaw: number; rot?: [number, number, number]; s?: [number, number, number]; }
export interface WorldData {
  version: number;
  play_area: { x: [number, number]; z: [number, number] };
  districts: { name: string; x: [number, number]; z: [number, number] }[];
  taxi_ranks: { name: string; p: [number, number, number] }[];
  volcano: [number, number, number];
  landmarks: { name: string; p: [number, number, number]; yaw: number; mesh: string }[];
  bus_stops: { p: [number, number, number]; yaw: number }[];
  traffic_lights: { p: [number, number, number]; yaw: number }[];
  instances: WorldInstance[];
}

export interface World {
  data: WorldData;
  ground: Ground;
  root: TransformNode;
  sources: Map<string, Mesh[]>;
  instances: InstancedMesh[];
  missing: string[];
  dispose(): void;
}

const deg = Math.PI / 180;

/** Flatten a loaded glTF container: unparent every mesh primitive, bake the node/loader transforms into
 *  vertices, hide. Multi-material nodes arrive as N "_primitiveN" meshes, grouped under the node name. */
function flattenContainer(meshes: Mesh[]): Map<string, Mesh[]> {
  const out = new Map<string, Mesh[]>();
  for (const m of meshes) {
    if (!(m instanceof Mesh) || !m.geometry) continue;
    m.setParent(null);
    m.bakeCurrentTransformIntoVertices();
    // The loader's handedness fix is a 180-degree turn plus a z mirror on the root; flattening keeps only the
    // mirror, which left every piece turned 180 degrees about its pivot (edge-pivoted road tiles ended up one
    // tile off, buildings did not match their Blender-baked colliders). Turn the geometry back so the mesh's
    // local frame matches the asset (and the stamps): +z is the tile's long side at yaw 0.
    m.bakeTransformIntoVertices(Matrix.RotationY(Math.PI));
    m.refreshBoundingInfo();
    m.position.setAll(0); m.rotationQuaternion = null; m.rotation.setAll(0); m.scaling.setAll(1);
    m.isVisible = false;
    m.isPickable = false;
    m.doNotSyncBoundingInfo = true;
    const key = m.name.replace(/_primitive\d+$/, '');
    let list = out.get(key);
    if (!list) out.set(key, (list = []));
    list.push(m);
  }
  return out;
}

/** The shared mesh library (city.glb) loaded once; levels instance from it. */
export class CityLibrary {
  private constructor(readonly sources: Map<string, Mesh[]>) {}
  static async load(scene: Scene, shadows: CascadedShadowGenerator): Promise<CityLibrary> {
    const container = await LoadAssetContainerAsync(`${ASSET_BASE}models/city.glb`, scene);
    container.addAllToScene();
    const sources = flattenContainer(container.meshes as Mesh[]);
    for (const mat of container.materials) {
      if (mat instanceof PBRMaterial) { mat.metallic = 0; mat.roughness = 1; mat.specularIntensity = 0.15; mat.enableSpecularAntiAliasing = false; mat.ambientColor = new Color3(1, 1, 1); mat.backFaceCulling = true; }
      mat.freeze();
    }
    const landscapeMaterials = new Map<PBRMaterial, PBRMaterial>();
    for (const [name, prims] of sources) for (const src of prims) {
      // The boundary hills are open, two-sided scenery. Culling their backs leaves
      // triangular holes when viewed from inside the city or across the bay.
      if (name.startsWith('SM_Nature_Landscape_') && src.material instanceof PBRMaterial) {
        let material = landscapeMaterials.get(src.material);
        if (!material) {
          material = src.material.clone(`${src.material.name}_landscape`);
          material.backFaceCulling = false; material.twoSidedLighting = true; material.freeze();
          landscapeMaterials.set(src.material, material);
        }
        src.material = material;
      }
      src.receiveShadows = true;
      if (/SM_Buildings_|SM_Nature_Tree|SM_Props_|SM_IndustrialProps|SM_FloorProps/.test(name)) shadows.addShadowCaster(src, false);
    }
    return new CityLibrary(sources);
  }
}

export interface LevelSource { data: WorldData; ground: Ground; }

/** Static level: baked files under public/levels/<id>/. */
export async function fetchLevel(id: string, version = ''): Promise<LevelSource> {
  const base = `${ASSET_BASE}levels/${id}/`; const q = version ? `?v=${encodeURIComponent(version)}` : '';
  const [data, ground] = await Promise.all([fetch(`${base}world.json${q}`).then((r) => r.json() as Promise<WorldData>), Ground.load(base, fetch, version)]);
  return { data, ground };
}

export async function loadWorld(scene: Scene, lib: CityLibrary, level: LevelSource, onProgress?: (label: string) => void): Promise<World> {
  const { data, ground } = level;
  const sources = lib.sources;
  onProgress?.('Building the streets');
  const root = new TransformNode('city', scene);
  const instances: InstancedMesh[] = [];
  const missing = new Set<string>();
  for (const inst of data.instances) {
    const prims = sources.get(inst.m);
    if (!prims) { missing.add(inst.m); continue; }
    const lift = floorLift(inst.m);
    for (const src of prims) {
      const im = src.createInstance(inst.m);
      im.position.set(inst.p[0], inst.p[1] + lift, inst.p[2]);
      if (inst.rot) im.rotation.set(-inst.rot[0] * deg, inst.rot[1] * deg, inst.rot[2] * deg);
      else im.rotation.set(0, inst.yaw * deg, 0);
      if (inst.s) im.scaling.set(inst.s[0], inst.s[2], inst.s[1]);
      im.parent = root;
      im.isPickable = false;
      im.alwaysSelectAsActiveMesh = false;
      im.freezeWorldMatrix();
      instances.push(im);
    }
  }
  // solid ground under everything (the Unreal landscape was not a static mesh) plus water only where the
  // surface mask says there is no floor at all: the bay and the sea beyond the mountains
  const groundPlane = MeshBuilder.CreateGround('groundPlane', { width: 3000, height: 3000 }, scene);
  groundPlane.position.set(0, -1, 100); // seabed must sit below the water at -0.6
  const gm = new StandardMaterial('groundPlane', scene);
  gm.diffuseColor = new Color3(0.36, 0.52, 0.3); gm.specularColor = Color3.Black();
  groundPlane.material = gm; groundPlane.isPickable = false; groundPlane.receiveShadows = true; groundPlane.freezeWorldMatrix();
  const water = buildWaterMesh(scene, ground);
  const wm = new StandardMaterial('water', scene);
  wm.diffuseColor = new Color3(0.16, 0.5, 0.82); wm.specularColor = new Color3(0.5, 0.5, 0.5); wm.specularPower = 48; wm.emissiveColor = new Color3(0.02, 0.08, 0.16);
  water.material = wm; water.isPickable = false; water.receiveShadows = true; water.freezeWorldMatrix();

  onProgress?.(`City ready: ${instances.length} pieces`);
  return { data, ground, root, sources, instances, missing: [...missing], dispose() { for (const im of instances) im.dispose(); root.dispose(); groundPlane.dispose(); water.dispose(); } };
}

/** Coplanar floor pieces z-fight: the pack stacks pavement and plaza tiles on road tiles at the same
 *  height. Lift each category a little in a fixed order so the surface meant to be on top always wins. */
function floorLift(mesh: string): number {
  if (/SM_Floor_Road|SM_Floor_Bridge/.test(mesh)) return 0;
  if (/SM_Floor_Parking|SM_Floor_Airport|SM_Floor_Port|SM_Floor_Railway|SM_Floor_Metro/.test(mesh)) return 0.012;
  if (/SM_Floor_Tile|SM_Floor_Sand/.test(mesh)) return 0.024;
  if (/SM_Floor_Garden|SM_Floor_Park/.test(mesh)) return 0.036;
  return 0;
}

/** One flat mesh covering every 'none' cell of the surface mask (run-length quads per row), at sea level. */
function buildWaterMesh(scene: Scene, ground: Ground): Mesh {
  const positions: number[] = []; const indices: number[] = []; const normals: number[] = [];
  const y = -0.6; const c0x = ground.origin[0], c0z = ground.origin[1], cell = ground.cell;
  let quad = 0;
  const pushQuad = (x0: number, z0: number, x1: number, z1: number) => {
    positions.push(x0, y, z0, x1, y, z0, x1, y, z1, x0, y, z1);
    normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
    // Babylon's left-handed winding: visible from above, like CreateGround.
    const b = quad * 4; indices.push(b, b + 1, b + 2, b, b + 2, b + 3); quad++;
  };
  for (let r = 0; r < ground.rows; r++) {
    let start = -1;
    for (let c = 0; c <= ground.cols; c++) {
      const none = c < ground.cols && ground.types[r * ground.cols + c] === 0;
      if (none && start < 0) start = c;
      if (!none && start >= 0) { pushQuad(c0x + start * cell, c0z + r * cell, c0x + c * cell, c0z + (r + 1) * cell); start = -1; }
    }
  }
  // sea beyond the mask on the west and south, where the map shows open water
  pushQuad(c0x - 1200, c0z - 1200, c0x, c0z + ground.rows * cell + 1200);
  pushQuad(c0x, c0z - 1200, c0x + ground.cols * cell + 1200, c0z);
  const m = new Mesh('water', scene);
  const vd = new VertexData(); vd.positions = positions; vd.indices = indices; vd.normals = normals; vd.applyToMesh(m);
  return m;
}

export function playAreaCentre(data: WorldData): Vector3 {
  return new Vector3((data.play_area.x[0] + data.play_area.x[1]) / 2, 0, (data.play_area.z[0] + data.play_area.z[1]) / 2);
}
